import { createApi } from '@reduxjs/toolkit/query/react'

import {
  BotRateMeta,
  BotSoldMilestoneMeta,
  CompetitionEndsApplicationMeta,
  CompetitionOpensApplicationMeta,
  CompetitionStartsApplicationMeta,
  FolloweeAppliedCompetitionMeta,
  FolloweePublishedBotMeta,
  FolloweeUpdateBotMeta,
  NewCompetitionPublishedMeta,
  NewFollowerMeta,
  NotificationType,
  PurchasedBotDeleteMeta,
  PurchasedBotUpdateMeta,
} from '@/constants/notification-type'
import {
  infiniteOffsetPaginationQueryOptions,
  OffsetPaginationRequest,
  PaginationResponse,
} from '@/model/pagination'
import { baseQuery } from '@/redux/utils'

export interface Notification {
  createdAt: number
  id: number
  isRead: boolean
  meta:
    | NewFollowerMeta
    | FolloweePublishedBotMeta
    | FolloweeUpdateBotMeta
    | FolloweeAppliedCompetitionMeta
    | NewCompetitionPublishedMeta
    | CompetitionOpensApplicationMeta
    | CompetitionStartsApplicationMeta
    | CompetitionEndsApplicationMeta
    | PurchasedBotUpdateMeta
    | PurchasedBotDeleteMeta
    | BotSoldMilestoneMeta
    | BotRateMeta
  type: NotificationType
}

export interface ReadAllNotificationsResponse
  extends PaginationResponse<Notification> {
  unreadCount: number
}

export interface UpdateNotificationSettingsRequest {
  followUpdate?: boolean
  updatesFromFollowee?: boolean
  competitionUpdate?: boolean
  botUpdate?: boolean
}

export interface NotificationSettingsResponse {
  followUpdate: boolean
  updatesFromFollowee: boolean
  competitionUpdate: boolean
  botUpdate: boolean
}

export const notificationSlice = createApi({
  reducerPath: 'notification',
  baseQuery,
  tagTypes: ['Notification'],
  endpoints: (builder) => ({
    readAllNotifications: builder.infiniteQuery<
      ReadAllNotificationsResponse,
      void,
      OffsetPaginationRequest
    >({
      infiniteQueryOptions: infiniteOffsetPaginationQueryOptions,
      query: ({ pageParam }) => ({
        url: '/notification/all',
        params: pageParam,
      }),
    }),
    markOneNotificationAsRead: builder.mutation<void, number>({
      query: (id) => ({
        url: `/notification/${id}/mark-as-read`,
        method: 'POST',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(
          notificationSlice.util.updateQueryData(
            'readAllNotifications',
            undefined,
            (draft) => {
              draft.pages.forEach((page) => {
                page.unreadCount = page.unreadCount - 1
                page.rows.forEach((item) => {
                  if (item.id === id) {
                    item.isRead = true
                  }
                })
              })
            },
          ),
        )
      },
    }),
    markMineNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({
        url: '/notification/mine/mark-all-as-read',
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(
          notificationSlice.util.updateQueryData(
            'readAllNotifications',
            undefined,
            (draft) => {
              draft.pages.forEach((page) => {
                page.unreadCount = 0
                page.rows.forEach((item) => {
                  item.isRead = true
                })
              })
            },
          ),
        )
      },
    }),
    readNotificationSettings: builder.query<NotificationSettingsResponse, void>(
      {
        query: () => '/notification/settings',
      },
    ),
    updateNotificationSettings: builder.mutation<
      NotificationSettingsResponse,
      UpdateNotificationSettingsRequest
    >({
      query: (settings) => ({
        url: '/notification/settings',
        method: 'PUT',
        body: settings,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const data = await queryFulfilled
        dispatch(
          notificationSlice.util.updateQueryData(
            'readNotificationSettings',
            undefined,
            (draft) => {
              draft.followUpdate = data.data.followUpdate
              draft.updatesFromFollowee = data.data.updatesFromFollowee
              draft.competitionUpdate = data.data.competitionUpdate
              draft.botUpdate = data.data.botUpdate
            },
          ),
        )
      },
    }),
  }),
})

export const {
  useReadAllNotificationsInfiniteQuery,
  useMarkOneNotificationAsReadMutation,
  useMarkMineNotificationsAsReadMutation,
  useReadNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} = notificationSlice
